import React, { createContext, useContext, useState, useEffect } from 'react';

interface User {
  id: string;
  email: string;
  orgId: string;
  role: 'user' | 'admin';
}

interface AuthContextType {
  user: User | null;
  anonymousId: string;
  login: (email: string) => void;
  logout: () => void;
  isOrgMode: boolean;
  setOrgMode: (mode: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [anonymousId, setAnonymousId] = useState(() => {
    let id = localStorage.getItem('lail_anon_id');
    if (!id) {
      id = 'anon-' + Math.random().toString(36).substring(2, 10);
      localStorage.setItem('lail_anon_id', id);
    }
    return id;
  });
  const [isOrgMode, setOrgMode] = useState(() => localStorage.getItem('lail-org-mode') === 'true');

  useEffect(() => {
    localStorage.setItem('lail-org-mode', isOrgMode.toString());
    
    const savedSession = localStorage.getItem('lail-session');
    if (savedSession) {
      try {
        setUser(JSON.parse(savedSession));
      } catch (e) {
        localStorage.removeItem('lail-session');
      }
    }
  }, [isOrgMode]);

  const login = (email: string) => {
    const mockUser: User = {
      id: btoa(email),
      email,
      orgId: 'arctic-corp-01',
      role: email.includes('admin') ? 'admin' : 'user'
    };
    setUser(mockUser);
    localStorage.setItem('lail-session', JSON.stringify(mockUser));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('lail-session');
  };

  return (
    <AuthContext.Provider value={{ user, anonymousId, login, logout, isOrgMode, setOrgMode }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
